import './RadioCard.scss';

import { Circle, RadioButton } from '@phosphor-icons/react';
import { ChangeEvent, useRef } from 'react';

import { useClassHelper } from '../../hooks';
import { appTypes } from '../../types/common';
import { Stack } from '../Stack';

const componentName = 'c-RadioCard';

interface RadioProps {
  name: string;
  title: string;
  value: string;
  description: string;
  descriptionExtended: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (ev: ChangeEvent<HTMLInputElement>) => void;
  isPortalPreview?: boolean;
  app?: keyof typeof appTypes;
  colors?: string[];
  'data-dd-action-name'?: string;
}

export const RadioCard = (props: RadioProps) => {
  const c = useClassHelper(componentName);
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    name,
    title,
    value,
    description,
    descriptionExtended,
    checked,
    disabled = false,
    isPortalPreview = false,
    onChange,
    app,
    colors = [],
    'data-dd-action-name': dataDdActionName,
  } = props;

  const id = `${name}-${value}`;
  const [colorSelected, colorUnselected] = colors;

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (inputRef.current && !disabled) {
        inputRef.current.click();
      }
    }
  };

  return (
    <div
      role="radio"
      aria-checked={checked}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      className={`${c('base', {
        disabled: disabled,
        __active: checked,
        [`--${app}`]: Boolean(app),
      })} focus-visible:focus-ring-form`}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={handleKeyPress}
    >
      <label
        htmlFor={id}
        className={c('__inner', { '__inner-preview': isPortalPreview })}
      >
        <Stack direction="row" align="center" gap={isPortalPreview ? 1.5 : 3}>
          <input
            id={id}
            type="radio"
            ref={inputRef}
            checked={checked}
            value={value}
            name={name}
            onChange={onChange}
            className={c('__input')}
            tabIndex={-1}
            data-dd-action-name={dataDdActionName}
          />
          <div className={c('__icon')}>
            {checked ? (
              <RadioButton
                size={isPortalPreview ? 16 : 24}
                weight="fill"
                color={colorSelected}
              />
            ) : (
              <Circle
                size={isPortalPreview ? 16 : 24}
                weight="regular"
                color={colorUnselected}
              />
            )}
          </div>
          <Stack
            direction="column"
            gap={1}
            className={c('__text', { '__text-preview': isPortalPreview })}
          >
            <h3>{title}</h3>
            <h4>{description}</h4>
            <p>{descriptionExtended}</p>
          </Stack>
        </Stack>
      </label>
    </div>
  );
};
